import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, switchMap, throwError } from 'rxjs';
import { ToastService } from '../../shared/services/toast';
import { ResponseModel } from '../../shared/models/response';
import { TRANSLATION_KEYS } from '../../shared/i18n/translation-keys';
import { AuthSession } from '../auth/auth-session';

const I18N_ASSETS_PATH = '/assets/i18n/';
export const SILENT_HTTP_ERRORS = new HttpContextToken<boolean>(() => false);

interface UserManagementErrorBody extends ResponseModel<unknown> {
  messages?: Array<{ message?: string }>;
}

function backendErrorMessage(body: UserManagementErrorBody | undefined): string | null {
  if (body?.errorMessages) return body.errorMessages;

  const messages = body?.messages
    ?.map(({ message }) => message?.trim())
    .filter((message): message is string => !!message);
  return messages?.length ? messages.join('\n') : null;
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.url.includes(I18N_ASSETS_PATH)) {
    return next(req);
  }

  const toast = inject(ToastService);
  const translate = inject(TranslateService);
  const session = inject(AuthSession);

  return next(req).pipe(
    map((event) => {
      const body = (event as any)?.body as ResponseModel<unknown> | undefined;
      if (body?.errorMessages) {
        toast.error(body.errorMessages);
      }
      return event;
    }),
    catchError((error: HttpErrorResponse) => {
      if (req.headers.has('Authorization') && error.status === 401) {
        session.clear();
        return throwError(() => error);
      }

      if (req.headers.has('Authorization') && error.status === 403) {
        toast.error(translate.instant(TRANSLATION_KEYS.AUTH.FORBIDDEN));
        return throwError(() => error);
      }

      if (req.context.get(SILENT_HTTP_ERRORS)) {
        return throwError(() => error);
      }

      const body = error.error as UserManagementErrorBody | undefined;
      const message = backendErrorMessage(body);

      if (message) {
        toast.error(message);
        return throwError(() => error);
      }

      const key =
        error.status === 0
          ? TRANSLATION_KEYS.GENERAL_ERROR.CONNECTION_ERROR
          : TRANSLATION_KEYS.GENERAL_ERROR.GENERIC_ERROR;

      return translate.get(key).pipe(
        switchMap((message) => {
          toast.error(message);
          return throwError(() => error);
        }),
      );
    }),
  );
};
